import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ElementaryMissionPage from "./ElementaryMissionPage";
import LemonBotJr from "@/components/LemonBotJr";
import MissionCompleteOverlay from "@/components/MissionCompleteOverlay";
import {
  loadJrState,
  saveJrState,
  markMissionComplete,
  JR_BRAND_COLORS,
  JR_SIGN_SLOGANS,
} from "@/utils/jrDemoState";
import { playClick, playCorrect } from "@/utils/jrSound";
import { speak } from "@/utils/jrVoice";
import { Megaphone, PencilLine } from "lucide-react";

export default function Mission7() {
  const navigate = useNavigate();
  const initial = loadJrState();
  const [slogan, setSlogan] = useState(initial.signSlogan || "");
  const [custom, setCustom] = useState(false);
  const [bg, setBg] = useState(initial.signBg || "sunny");
  const [accent, setAccent] = useState(initial.signAccent || "berry");
  const [showOverlay, setShowOverlay] = useState(false);

  const bgColor = JR_BRAND_COLORS.find((c) => c.key === bg) || JR_BRAND_COLORS[0];
  const accentColor = JR_BRAND_COLORS.find((c) => c.key === accent) || JR_BRAND_COLORS[2];
  const brandName = initial.brandName || initial.businessIdea || "My Business";

  const canContinue = slogan.trim().length >= 3;

  const onContinue = () => {
    if (!canContinue) return;
    const isPresetSlogan = JR_SIGN_SLOGANS.includes(slogan.trim());
    saveJrState({
      signSlogan: slogan.trim(),
      signBg: bg,
      signAccent: accent,
    });
    markMissionComplete(7, { customized: !isPresetSlogan });
    playCorrect();
    speak(`Your sign looks great! ${slogan.trim()}. Time to tell the world!`);
    setShowOverlay(true);
  };

  const pickSlogan = (s) => {
    setSlogan(s);
    setCustom(false);
    playClick();
  };

  return (
    <ElementaryMissionPage
      missionNumber={7}
      title="Tell the World"
      lesson="A sign tells people what you do and why they should care. Pick a slogan and colors that pop!"
      canContinue={canContinue}
      onContinue={onContinue}
      continueLabel="Save My Sign"
      testId="elementary-mission-7-page"
    >
      <div className="mb-5">
        <LemonBotJr
          tone="green"
          greeting="Let's make a sign that grabs attention! Pick a short slogan and colors kids will notice."
          prompts={[
            "A short slogan is easier to remember.",
            "Bold colors stand out from far away.",
            "Your brand name and slogan work together!",
          ]}
          compact
        />
      </div>

      {/* Live sign preview */}
      <div
        className={`mb-6 ${bgColor.bg} rounded-3xl p-8 ring-4 ring-yellow-400 shadow-xl relative overflow-hidden`}
        data-testid="mission-7-preview"
      >
        <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/70 rounded-full px-2.5 py-1 text-[10px] font-bold text-gray-700 uppercase tracking-wide">
          <Megaphone className="w-3 h-3" /> Live Sign Preview
        </div>
        <div className="mt-6 text-center">
          <p className={`${accentColor.text} text-2xl sm:text-3xl font-black uppercase tracking-wide mb-2 drop-shadow`} data-testid="mission-7-preview-brand">
            {brandName}
          </p>
          <div className={`${accentColor.bg} inline-block px-5 py-2 rounded-2xl shadow`}>
            <p className="text-white text-lg sm:text-2xl font-black" data-testid="mission-7-preview-slogan">
              {slogan.trim() || "Your slogan goes here!"}
            </p>
          </div>
        </div>
        {/* Deco dots */}
        <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-white/30" aria-hidden="true" />
        <div className="absolute -bottom-6 -left-6 w-20 h-20 rounded-full bg-white/20" aria-hidden="true" />
      </div>

      {/* Slogan picker */}
      <div className="mb-6">
        <p className="text-base font-bold text-gray-900 mb-3">Pick a slogan</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {JR_SIGN_SLOGANS.map((s) => {
            const selected = !custom && slogan === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => pickSlogan(s)}
                className={`rounded-2xl px-4 py-3 text-left text-sm font-semibold ring-2 transition-all ${
                  selected
                    ? "bg-yellow-50 ring-yellow-500 shadow"
                    : "bg-white ring-slate-200 hover:ring-yellow-400 hover:shadow-md"
                }`}
                data-testid={`mission-7-slogan-${s.slice(0, 10).replace(/\s+/g, "-")}`}
                aria-pressed={selected}
              >
                {s}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => {
            setCustom(true);
            setSlogan("");
            playClick();
          }}
          className="mt-3 text-xs font-bold text-yellow-700 hover:text-yellow-900 inline-flex items-center gap-1"
          data-testid="mission-7-write-own"
        >
          <PencilLine className="w-3 h-3" />
          Write my own slogan
        </button>
        {custom && (
          <input
            type="text"
            value={slogan}
            onChange={(e) => setSlogan(e.target.value)}
            maxLength={60}
            placeholder="My slogan is…"
            className="mt-3 w-full px-4 py-3 border-2 border-yellow-200 rounded-2xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
            data-testid="mission-7-custom-slogan"
            autoFocus
          />
        )}
      </div>

      {/* Background color */}
      <div className="mb-6">
        <p className="text-base font-bold text-gray-900 mb-3">Background color</p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {JR_BRAND_COLORS.map((c) => {
            const selected = bg === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => {
                  setBg(c.key);
                  playClick();
                }}
                className={`${c.bg} rounded-2xl aspect-square ring-4 transition-all flex items-center justify-center ${
                  selected
                    ? `${c.ring} scale-105 shadow-lg`
                    : "ring-white hover:scale-105"
                }`}
                data-testid={`mission-7-bg-${c.key}`}
                aria-pressed={selected}
                aria-label={`${c.name} background`}
              >
                {selected && (
                  <span className="bg-white/80 rounded-full w-7 h-7 flex items-center justify-center text-gray-900 font-black">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Accent color */}
      <div className="mb-2">
        <p className="text-base font-bold text-gray-900 mb-3">Slogan color</p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {JR_BRAND_COLORS.map((c) => {
            const selected = accent === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => {
                  setAccent(c.key);
                  playClick();
                }}
                className={`${c.bg} rounded-2xl aspect-square ring-4 transition-all flex items-center justify-center ${
                  selected
                    ? `${c.ring} scale-105 shadow-lg`
                    : "ring-white hover:scale-105"
                }`}
                data-testid={`mission-7-accent-${c.key}`}
                aria-pressed={selected}
                aria-label={`${c.name} accent`}
              >
                {selected && (
                  <span className="bg-white/80 rounded-full w-7 h-7 flex items-center justify-center text-gray-900 font-black">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {showOverlay && (
        <MissionCompleteOverlay
          missionNumber={7}
          missionTitle="Tell the World"
          message={`Your sign pops! "${slogan.trim()}" is ready to grab attention everywhere.`}
          continueLabel="Back to CEO City"
          onContinue={() => navigate("/elementary/demo")}
        />
      )}
    </ElementaryMissionPage>
  );
}
